/**
 * The API contract, as Zod schemas.
 *
 * Every response is parsed rather than cast. A cast is a promise the compiler
 * cannot keep: if the backend renames a field, `as Account` keeps compiling and the
 * portal renders `undefined` where a plan name should be. Parsing turns that into
 * one clear error at the boundary.
 *
 * Fields the UI does not use are omitted; `.passthrough()` is not used, so an
 * extra field from a newer backend is dropped silently rather than breaking the
 * page — additive backend changes must not require a frontend deploy.
 */

import { z } from 'zod'

// Every plan the API can name. `free` is what an account with nothing behind it
// resolves to and `enterprise` is quoted rather than sold — both are rendered from
// the same catalog rows as Team, because the comparison matrix and the signed
// lease have to come from one list.
export const planCode = z.enum(['free', 'team', 'enterprise'])
export type PlanCode = z.infer<typeof planCode>

export const billingInterval = z.enum(['monthly', 'annual'])
export type BillingInterval = z.infer<typeof billingInterval>

export const roleSchema = z.enum(['owner', 'admin', 'billing_admin', 'member'])
export type Role = z.infer<typeof roleSchema>

/** Human labels for roles, kept next to the enum so they cannot drift apart. */
/**
 * What a plan code is called in front of a customer.
 *
 * `free_team` is the workspace form of the Free tier, not a fourth tier — the
 * distinction is ours, so both print "Free". Anything unknown falls back to the
 * code itself rather than to an empty cell.
 */
const planLabels: Record<string, string> = {
  free: 'Free',
  free_team: 'Team Free',
  team: 'Team',
  enterprise: 'Enterprise',
}

export function planLabel(code: string | undefined): string {
  if (!code) return '—'
  return planLabels[code] ?? code.replace(/_/g, ' ')
}

export const roleLabels: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  billing_admin: 'Billing admin',
  member: 'Member',
}

export const subscriptionStatus = z.enum([
  'active',
  'grace_payment',
  'suspended',
  'canceled',
  'expired',
])
export type SubscriptionStatus = z.infer<typeof subscriptionStatus>


export const membershipRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: roleSchema,
  status: z.string().optional(),
  has_seat: z.boolean(),
})
export type MembershipRef = z.infer<typeof membershipRefSchema>

export const accountSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string(),
  photo_url: z.string().optional(),
  providers: z.array(z.string()),
  status: z.string(),
  accepted_terms: z.boolean(),
  plan: z.string().optional(),
  subscription_status: z.string().optional(),
  has_access: z.boolean(),
  organizations: z.array(membershipRefSchema),
  /**
   * Deployment capabilities rather than facts about this account. Defaulted so a
   * portal built against a newer API still renders against an older one — and
   * defaulted to the RESTRICTIVE value, so a missing field hides a control rather
   * than offering one the backend will refuse.
   */
  purchases_enabled: z.boolean().default(false),
  support_attachments_enabled: z.boolean().default(false),
})
export type Account = z.infer<typeof accountSchema>

export const deviceSchema = z.object({
  id: z.string(),
  user_id: z.string().optional(),
  display_name: z.string(),
  platform: z.string(),
  arch: z.string(),
  cli_version: z.string(),
  /**
   * A device is one installation, for as long as the account exists — never a
   * row that gets deleted. `revoked` is switched off, `removed` is out of
   * sight; both are reversible, and both keep everything the row knows. The
   * API does not list `removed` ones, so it is here for completeness rather
   * than to be rendered.
   */
  status: z.enum(['active', 'revoked', 'removed']),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  revoked_at: z.string().nullish(),
  /** An administrator revoked this one, so its owner cannot bring it back. */
  revoked_by_admin: z.boolean().optional(),
  /** How many times this installation has been switched on, counting the first. */
  activation_count: z.number().optional(),
  /** The version it first checked in with, beside the one it last ran. */
  first_cli_version: z.string().optional(),
  current: z.boolean().optional(),
  /** The one row a machine token speaks through; every container presenting it. */
  ephemeral: z.boolean().optional(),
  machine_token_id: z.string().optional(),
  /**
   * Active, but silent for longer than the lease grace (72 hours). Still listed
   * and still `active`; it does not count against the device limit until it
   * checks in again, and it is refused then if the limit has filled meanwhile.
   */
  dormant: z.boolean().optional(),
})
export type Device = z.infer<typeof deviceSchema>

/**
 * An offline licence the workspace has issued, from
 * `GET /v1/organizations/{id}/offline-licenses`.
 *
 * Revocation does not reach a machine with no network, so this list is the
 * admin's whole view of what is out there: `revoked` says the device row was
 * revoked, and the machine keeps working regardless until `expires_at`.
 */
export const offlineLicenseSchema = z.object({
  device_id: z.string(),
  display_name: z.string(),
  platform: z.string(),
  arch: z.string(),
  cli_version: z.string(),
  user_id: z.string(),
  expires_at: z.string(),
  expired: z.boolean(),
  revoked: z.boolean(),
})
export type OfflineLicense = z.infer<typeof offlineLicenseSchema>

// --- CAVS DB Server -------------------------------------------------------------

/** One server that checked in with its licence. Never anything about what it holds. */
export const cavsdbInstallationSchema = z.object({
  id: z.string(),
  first_seen: z.string(),
  last_seen: z.string(),
  version: z.string().optional(),
  os: z.string().optional(),
  arch: z.string().optional(),
})
export type CavsdbInstallation = z.infer<typeof cavsdbInstallationSchema>

/**
 * A CAVS DB Server licence record, from
 * `GET /v1/organizations/{id}/cavsdb/licenses`. The token itself is never part
 * of it: tokens are re-signed on demand and never stored.
 */
export const cavsdbLicenseSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  status: z.enum(['active', 'superseded', 'revoked']),
  closed_network: z.boolean(),
  created_by: z.string(),
  created_at: z.string(),
  issued_at: z.string(),
  expires_at: z.string(),
  superseded_by: z.string().optional(),
  overlap_until: z.string().optional(),
  revoked_at: z.string().optional(),
  revoked_by: z.string().optional(),
  revoke_reason: z.string().optional(),
  key_id: z.string(),
  limits: z.record(z.number()),
  offline_grace_days: z.number(),
  installations: z.array(cavsdbInstallationSchema).nullable().transform((rows) => rows ?? []),
})
export type CavsdbLicense = z.infer<typeof cavsdbLicenseSchema>

/** The one response that carries a token unasked: the moment of issue. */
export const cavsdbIssuedSchema = z.object({
  license: cavsdbLicenseSchema,
  token: z.string(),
  superseded: z.array(z.string()).optional(),
})
export type CavsdbIssued = z.infer<typeof cavsdbIssuedSchema>

export const cavsdbTokenSchema = z.object({
  license_id: z.string(),
  token: z.string(),
  expires_at: z.string(),
})
export type CavsdbToken = z.infer<typeof cavsdbTokenSchema>

/** The newest server release for a channel, from `GET …/cavsdb/downloads`. */
export const cavsdbDownloadsSchema = z.object({
  version: z.string(),
  channel: z.string(),
  notes: z.string().optional(),
  notes_url: z.string().optional(),
  published_at: z.number(),
  artifacts: z.array(
    z.object({
      platform: z.string(),
      arch: z.string(),
      filename: z.string(),
      url: z.string(),
      size_bytes: z.number(),
      sha256: z.string(),
    }),
  ),
  manifest_signature: z.string(),
  signing_key_id: z.string(),
  signed_payload: z.string().optional(),
})
export type CavsdbDownloads = z.infer<typeof cavsdbDownloadsSchema>

/**
 * A machine token as the portal sees it: everything except the secret. The
 * secret exists for the length of one create response and is then only a hash,
 * which is why `expired` and `revoked` arrive derived — the server's clock, not
 * the browser's, decides whether a credential still works.
 */
export const machineTokenSchema = z.object({
  id: z.string(),
  name: z.string(),
  user_id: z.string(),
  organization_id: z.string().optional(),
  device_id: z.string().optional(),
  created_at: z.string(),
  expires_at: z.string(),
  last_used_at: z.string().nullish(),
  revoked_at: z.string().nullish(),
  expired: z.boolean(),
  revoked: z.boolean(),
})
export type MachineToken = z.infer<typeof machineTokenSchema>

export const subscriptionViewSchema = z.object({
  found: z.boolean(),
  plan_code: z.string().optional(),
  interval: z.string().optional(),
  status: z.string().optional(),
  seat_quantity: z.number().optional(),
  seats_assigned: z.number().optional(),
  current_period_end: z.string().optional(),
  cancel_at_period_end: z.boolean(),
  organization_id: z.string().optional(),
  grants_access: z.boolean(),
  manage_in_portal: z.boolean(),
  /**
   * Present on a monthly Team subscription while active-developer billing
   * (#930) is in shadow or on. In shadow, next_billed_seats is what the renewal
   * would be billed and Stripe still charges seat_quantity.
   */
  active_billing: z
    .object({
      mode: z.enum(['shadow', 'on']),
      period_start: z.string(),
      active_seats: z.number(),
      next_billed_seats: z.number(),
      last_billed_seats: z.number().optional(),
      last_active_seats: z.number().optional(),
    })
    .optional(),
})
export type SubscriptionView = z.infer<typeof subscriptionViewSchema>

/** A deny list and an allow list of path rules, in `.gitignore` syntax. */
export const pathRulesSchema = z.object({
  deny: z.array(z.string()).nullish(),
  allow: z.array(z.string()).nullish(),
})
export type PathRules = z.infer<typeof pathRulesSchema>

/**
 * The workspace read policy (#1164): what the CLI's MCP tools never serve to an
 * agent. Admin-typed configuration, signed into each member's license; the API
 * never sees a repository. Every part is optional, and `groups` is keyed by the
 * SCIM group's display name.
 */
export const readPolicySchema = z.object({
  paths: pathRulesSchema.optional(),
  groups: z.record(pathRulesSchema).optional(),
  secrets: z.object({ redact: z.boolean().optional() }).optional(),
})
export type ReadPolicy = z.infer<typeof readPolicySchema>

export const policiesSchema = z.object({
  update_channel: z.enum(['stable', 'beta', 'internal']),
  telemetry_mode: z.enum(['off', 'aggregate', 'full']),
  allowed_integrations: z.array(z.string()),
  offline_grace_hours: z.number(),
  diagnostic_uploads_allowed: z.boolean(),
  max_devices_per_member: z.number(),
  /** Absent when the workspace has none. Optional so an older API still parses. */
  read_policy: readPolicySchema.nullish(),
  /**
   * Group rules whose SCIM group no longer exists, listed apart by an API that
   * keys rules by group id (#1209). Read-only: they apply to nobody, the API
   * refuses the field on PUT, and saving a read policy without them drops them.
   */
  read_policy_stale_groups: z
    .array(
      z.object({
        group: z.string(),
        deny: z.array(z.string()).nullish(),
        allow: z.array(z.string()).nullish(),
      }),
    )
    .nullish(),
})
export type Policies = z.infer<typeof policiesSchema>

/**
 * GET /policies, with the version of each part that a save sends back as
 * If-Match (#1302). Empty from an API older than versions, and then no
 * If-Match is sent.
 */
export const policiesViewSchema = policiesSchema.extend({
  version: z.string().default(''),
  read_policy_version: z.string().default(''),
})
export type PoliciesView = z.infer<typeof policiesViewSchema>

/** The SCIM groups a read policy's per-group rules may key on. */
export const policyGroupsSchema = z.object({
  /** False when the plan has no SCIM: only workspace-wide rules can be saved. */
  available: z.boolean(),
  groups: z.array(z.object({ id: z.string(), display_name: z.string(), members: z.number() })),
})
export type PolicyGroups = z.infer<typeof policyGroupsSchema>

export const seatSummarySchema = z.object({
  purchased: z.number(),
  assigned: z.number(),
  reserved: z.number(),
  available: z.number(),
})

export const overviewSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  role: roleSchema,
  policies: policiesSchema,
  seats: seatSummarySchema,
  members: z.number(),
  plan: z.string(),
  subscription_status: z.string(),
  capabilities: z.record(z.boolean()),
  /**
   * Set while the workspace runs an Enterprise pilot. Optional and nullable so a
   * portal built against this API still renders against an older one.
   */
  pilot: z
    .object({
      ends_at: z.string(),
      days_left: z.number(),
    })
    .nullable()
    .optional(),
})
export type Overview = z.infer<typeof overviewSchema>

export const memberSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  role: roleSchema,
  // 'provisioned' is a SCIM-created member awaiting first sign-in — without it
  // here, one provisioning run makes the whole roster unparseable.
  status: z.enum(['active', 'suspended', 'removed', 'provisioned']),
  has_seat: z.boolean(),
  seat_id: z.string().optional(),
  joined_at: z.string(),
  removed_at: z.string().nullish(),
  /** Last use of this workspace's licence; only while active billing is recorded. */
  last_active_at: z.string().nullish(),
})
export type Member = z.infer<typeof memberSchema>

export const invitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: roleSchema,
  status: z.string(),
  expires_at: z.string(),
  created_at: z.string(),
  seat_id: z.string().optional(),
})
export type Invitation = z.infer<typeof invitationSchema>

export const seatSchema = z.object({
  id: z.string(),
  status: z.enum(['available', 'reserved_by_invitation', 'assigned', 'suspended']),
  member_id: z.string().optional(),
  member_email: z.string().optional(),
  assigned_at: z.string().nullish(),
})
export type Seat = z.infer<typeof seatSchema>

export const auditEventSchema = z.object({
  ID: z.string().optional(),
  id: z.string().optional(),
  action: z.string(),
  actor_type: z.string().optional(),
  actor_email: z.string().optional(),
  target_type: z.string().optional(),
  target_id: z.string().optional(),
  metadata: z.record(z.string()).nullish(),
  created_at: z.string(),
})
export type AuditEvent = z.infer<typeof auditEventSchema>

export const pendingActivationSchema = z.object({
  session_id: z.string(),
  user_code: z.string(),
  display_name: z.string(),
  platform: z.string(),
  arch: z.string(),
  cli_version: z.string(),
  expires_at: z.string(),
})
export type PendingActivation = z.infer<typeof pendingActivationSchema>

export const approveResultSchema = z.object({
  approved: z.boolean(),
  plan_code: z.string().optional(),
  /**
   * Where to send the browser next on the loopback flow: the CLI's own listener on
   * 127.0.0.1, carrying the one-time code. Absent on the device-code flow, where the
   * terminal finds out by polling.
   */
  redirect_to: z.string().optional(),
})
export type ApproveResult = z.infer<typeof approveResultSchema>

export const priceSchema = z.object({
  interval: billingInterval,
  cents: z.number(),
  currency: z.string(),
})

export const planSchema = z.object({
  code: planCode,
  display_name: z.string(),
  features: z.record(z.boolean()),
  limits: z.record(z.number()),
  min_paid_seats: z.number(),
  max_paid_seats: z.number().optional(),
  prices: z.array(priceSchema),
})
export type Plan = z.infer<typeof planSchema>

export const catalogSchema = z.object({
  plans: z.array(planSchema),
  offline_grace_hours: z.number(),
  /**
   * Discount an annual commitment earns over twelve monthly payments. Optional so
   * the portal still renders against an API revision that predates the field —
   * the pricing copy falls back to deriving it from the two amounts.
   */
  annual_discount_percent: z.number().optional(),
  /**
   * Whether there is anything to buy. Defaults to false: a page that offers a
   * purchase the API refuses is worse than one explaining there is nothing to buy
   * yet, so the safe reading of a missing field is "closed".
   */
  purchases_enabled: z.boolean().default(false),
  /**
   * Whether an account can only be created from an invitation we sent. Defaults to
   * FALSE, matching the API's own default now that Archis Free is the front door: a
   * missing field must not hide a sign-up the backend would have accepted.
   */
  invite_only_signups: z.boolean().default(false),
})
export type Catalog = z.infer<typeof catalogSchema>

// --- support ------------------------------------------------------------------

export const ticketStatus = z.enum(['open', 'closed'])
export type TicketStatus = z.infer<typeof ticketStatus>

export const attachmentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  content_type: z.string(),
  size_bytes: z.number(),
  width: z.number().optional(),
  height: z.number().optional(),
  /**
   * A short-lived signed read URL, minted per response. Absent when signing failed,
   * which the UI renders as a missing image rather than a broken page.
   */
  url: z.string().optional(),
})
export type Attachment = z.infer<typeof attachmentSchema>

export const ticketSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: ticketStatus,
  comment_count: z.number(),
  attachment_count: z.number(),
  last_activity_at: z.string(),
  created_at: z.string(),
  closed_at: z.string().nullish(),
})
export type Ticket = z.infer<typeof ticketSchema>

export const ticketCommentSchema = z.object({
  id: z.string(),
  author: z.enum(['customer', 'staff']),
  author_name: z.string(),
  body: z.string(),
  attachments: z.array(attachmentSchema).default([]),
  created_at: z.string(),
})
export type TicketComment = z.infer<typeof ticketCommentSchema>

export const ticketDetailSchema = ticketSchema.extend({
  body: z.string(),
  attachments: z.array(attachmentSchema).default([]),
  comments: z.array(ticketCommentSchema).default([]),
})
export type TicketDetail = z.infer<typeof ticketDetailSchema>

export const ticketPageSchema = z.object({
  tickets: z.array(ticketSchema),
  next_cursor: z.string().optional(),
})

export const uploadGrantSchema = z.object({
  key: z.string(),
  url: z.string(),
  headers: z.record(z.string()),
  expires_at: z.string(),
  max_bytes: z.number(),
})
export type UploadGrant = z.infer<typeof uploadGrantSchema>

export const telemetryPropertySchema = z.object({
  name: z.string(),
  kind: z.string(),
  allowed: z.array(z.string()).nullish(),
  required: z.boolean(),
})

export const telemetryCatalogSchema = z.object({
  schema: z.string(),
  events: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
      properties: z.array(telemetryPropertySchema),
    }),
  ),
  never_collected: z.array(z.string()),
})
export type TelemetryCatalog = z.infer<typeof telemetryCatalogSchema>

/**
 * The public aggregate: how far Archis reaches, and what it saved.
 *
 * Every figure is a floor. The store holds buckets, never numbers, and a bucket
 * cannot be added up — its lower bound can. So these understate by construction,
 * which is what makes them safe to print next to a claim.
 */
export const telemetryStatsSchema = z.object({
  installs: z.number(),
  repositories: z.number(),
  tokens_saved: z.number(),
  indexes: z.number(),
  sessions: z.number(),
  reduction: z.string().optional(),
  languages: z.array(z.object({ name: z.string(), value: z.number() })).optional(),
  models: z
    .array(
      z.object({
        name: z.string(),
        input_tokens: z.number(),
        output_tokens: z.number(),
        turns: z.number(),
      }),
    )
    .optional(),
  since: z.string(),
  generated_at: z.string(),
})
export type TelemetryStats = z.infer<typeof telemetryStatsSchema>

/** Feature keys, mirroring the backend's plan catalog. */
export const featureLabels: Record<string, string> = {
  'core.runtime': 'Compiler and runtime',
  'languages.all_stable': 'All production language frontends',
  'integrations.all_stable': 'All production agent integrations',
  'mcp.local': 'Local MCP / API endpoint',
  'team.workspace': 'Organization workspace',
  'team.seat_management': 'Seat management',
  'team.audit_log': 'Audit log',
  'team.policy_management': 'Organization policies',
  'team.managed_update_channel': 'Managed update channel',
  'team.federation': 'Cross-repository workspaces and context',
  'support.priority': 'Priority support',
  'enterprise.sso': 'SAML / OIDC single sign-on',
  'enterprise.scim': 'SCIM provisioning',
  'enterprise.spend_analytics': 'Usage & spend analytics',
  'enterprise.ci_preindex': 'CI pre-indexing',
  'enterprise.siem_streaming': 'SIEM audit streaming',
  'cavsdb.selfhost': 'Self-hosted CAVS DB Server',
  'support.sla': 'Support SLA',
}

// --- domain claims -------------------------------------------------------------

/**
 * A workspace's claim on an email domain and the state of its proof.
 *
 * `record_name` / `record_value` are the TXT record the admin publishes; they are
 * shown, not hidden, because the person reading the screen is the one who has to
 * put them in DNS. `check_error` is what the last attempt found — the difference
 * between "not propagated yet" and "wrong zone".
 */
export const domainViewSchema = z.object({
  name: z.string(),
  verified: z.boolean(),
  verified_at: z.string().optional(),
  record_name: z.string().optional(),
  record_value: z.string().optional(),
  checked_at: z.string().optional(),
  check_error: z.string().optional(),
  used_by_sso: z.boolean(),
  capture_enabled: z.boolean(),
})
export type DomainView = z.infer<typeof domainViewSchema>

// --- enterprise ---------------------------------------------------------------

export const ssoConfigSchema = z.object({
  provider_id: z.string(),
  domain: z.string(),
  required: z.boolean(),
  capture: z.boolean(),
})
export type SSOConfig = z.infer<typeof ssoConfigSchema>

/** SIEM streaming config as the portal sees it: the collector credential never travels back out. */
export const siemViewSchema = z.object({
  endpoint: z.string(),
  authorization_set: z.boolean(),
})
export type SIEMView = z.infer<typeof siemViewSchema>

export const preindexSettingsSchema = z.object({
  bucket: z.string(),
})
export type PreindexSettings = z.infer<typeof preindexSettingsSchema>

export const ssoDiscoverySchema = z.object({
  sso: z.boolean(),
  provider_id: z.string().optional(),
  required: z.boolean().optional(),
})
export type SSODiscovery = z.infer<typeof ssoDiscoverySchema>

export const orgTokenSchema = z.object({
  id: z.string(),
  kind: z.string(),
  created_at: z.string(),
  revoked_at: z.string().nullish(),
})
export type OrgToken = z.infer<typeof orgTokenSchema>

/** The order features appear in the comparison matrix. */
export const featureOrder = [
  'core.runtime',
  'languages.all_stable',
  'integrations.all_stable',
  'mcp.local',
  'team.workspace',
  'team.seat_management',
  'team.managed_update_channel',
  'team.policy_management',
  'team.audit_log',
  'team.federation',
  'support.priority',
]
